import { Drawer, styled } from '@mui/material';

import { AiIcon, CloseIcon, MenuIcon, SearchIcon } from '../../../icons';
import { IconButton } from '../../atoms/IconButton';
import { BrandLockup } from '../../molecules/BrandLockup';
import { AppShellAccountBlock } from './AppShellAccountBlock';
import { APP_SHELL_COMPACT_BREAKPOINT, APP_SHELL_TOP_OFFSET_VAR } from './appShellLayout';
import { type AppShellProps, useAppShell } from './useAppShell';

type Props = AppShellProps;

const SIDEBAR_WIDTH = 32;
const DRAWER_WIDTH = 36;
const TOP_BAR_HEIGHT = 7;

const Root = styled('div')(({ theme }) => ({
  [APP_SHELL_TOP_OFFSET_VAR]: theme.spacing(TOP_BAR_HEIGHT),
  display: 'flex',
  minHeight: '100dvh',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  [theme.breakpoints.up(APP_SHELL_COMPACT_BREAKPOINT)]: {
    [APP_SHELL_TOP_OFFSET_VAR]: '0px',
  },
}));

const Sidebar = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  alignSelf: 'flex-start',
  flexShrink: 0,
  width: theme.spacing(SIDEBAR_WIDTH),
  height: '100dvh',
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const DrawerPanel = styled('div')(({ theme }) => ({
  width: `min(${theme.spacing(DRAWER_WIDTH)}, calc(100vw - ${theme.spacing(TOP_BAR_HEIGHT)}))`,
  height: '100%',
  backgroundColor: theme.palette.background.paper,
}));

const SidebarContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
});

const SidebarHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
  minHeight: theme.spacing(TOP_BAR_HEIGHT),
  paddingInline: theme.spacing(2),
  paddingTop: `max(${theme.spacing(1)}, env(safe-area-inset-top))`,
  paddingBottom: theme.spacing(1),
}));

// Under the brand row: the workspace switcher alone, full width.
const WorkspaceRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  paddingInline: theme.spacing(2),
  paddingBottom: theme.spacing(1),
}));

const BrandSlot = styled('div')({
  flex: 1,
  minWidth: 0,
});

const Title = styled('span')(({ theme }) => ({
  ...theme.typography.h6,
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const TitleLink = styled('a')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  marginInline: theme.spacing(-1),
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  color: 'inherit',
  textDecoration: 'none',
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const TitleHint = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  flexShrink: 0,
}));

const SearchSlot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  flexShrink: 0,
  paddingInline: theme.spacing(1.5),
  paddingBottom: theme.spacing(1),
}));

// The AI tint is reserved to AI features: the entry reads as "ask", not as one more page.

const AssistantLink = styled('a')(({ theme }) => ({
  ...theme.typography.body2,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minHeight: theme.spacing(5.5),
  paddingInline: theme.spacing(1.5),
  border: `1px solid ${theme.alpha(theme.palette.ai.main, ENTRY_BORDER_OPACITY)}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.alpha(theme.palette.ai.main, ENTRY_TINT_OPACITY),
  color: theme.palette.ai.main,
  textDecoration: 'none',
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      borderColor: theme.palette.ai.main,
      backgroundColor: theme.alpha(theme.palette.ai.main, ENTRY_HOVER_OPACITY),
    },
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.ai.main}`,
    outlineOffset: 2,
  },
  '&[aria-current="page"]': {
    ...theme.typography.body2Emphasis,
    borderColor: theme.palette.ai.main,
    backgroundColor: theme.alpha(theme.palette.ai.main, ENTRY_SELECTED_OPACITY),
  },
}));

// Search and the assistant are the two ways in: both stand out from the navigation with the same
// tinted treatment, search in the dark text grey, the assistant in AI purple.
const ENTRY_TINT_OPACITY = 0.12;
const ENTRY_HOVER_OPACITY = 0.18;
const ENTRY_SELECTED_OPACITY = 0.22;
const ENTRY_BORDER_OPACITY = 0.32;

const SearchButton = styled('button')(({ theme }) => ({
  ...theme.typography.body2,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  minHeight: theme.spacing(5.5),
  margin: 0,
  paddingInline: theme.spacing(1.5),
  border: `1px solid ${theme.alpha(theme.palette.text.primary, ENTRY_BORDER_OPACITY)}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.alpha(theme.palette.text.primary, ENTRY_TINT_OPACITY),
  color: theme.palette.text.primary,
  cursor: 'pointer',
  textAlign: 'left',
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      borderColor: theme.palette.text.primary,
      backgroundColor: theme.alpha(theme.palette.text.primary, ENTRY_HOVER_OPACITY),
    },
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const SearchLabel = styled('span')({
  flex: 1,
  minWidth: 0,
});

const ShortcutHint = styled('kbd')(({ theme }) => ({
  ...theme.typography.caption,
  flexShrink: 0,
  paddingInline: theme.spacing(0.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  fontFamily: 'inherit',
}));

const Navigation = styled('nav')(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  paddingInline: theme.spacing(1),
  paddingBlock: theme.spacing(1),
}));

const SidebarFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  flexShrink: 0,
  paddingInline: theme.spacing(1),
  paddingTop: theme.spacing(1),
  paddingBottom: `max(${theme.spacing(1)}, env(safe-area-inset-bottom))`,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const Column = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
});

const TopBar = styled('header')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  flexShrink: 0,
  height: theme.spacing(TOP_BAR_HEIGHT),
  paddingInline: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Main = styled('main')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  padding: theme.spacing(2),
}));

/**
 * Application frame: a sidebar with the product brand, the workspace name, global actions, search, navigation,
 * a footer apart from the navigation (settings) and the account,
 * permanent from `md`; below `md` a top bar (menu, title, search, actions) opens the same sidebar as a drawer.
 * Exposes the top bar height as `--app-shell-top-offset` for full-height pages (`PageLayout fixedHeader`).
 */
export function AppShell(props: Props) {
  const hook = useAppShell(props);

  const workspace = props.switchSpace ? (
    <TitleLink href={props.switchSpace.href} onClick={hook.handleSwitchSpaceClick}>
      <Title>{props.title}</Title>
      <TitleHint>{props.switchSpace.label}</TitleHint>
    </TitleLink>
  ) : (
    <Title>{props.title}</Title>
  );

  const headerEnd = hook.isCompact ? (
    <IconButton tooltip={hook.closeNavigationLabel} edge="end" onClick={hook.handleCloseDrawer}>
      <CloseIcon />
    </IconButton>
  ) : props.actions ? (
    <Actions>{props.actions}</Actions>
  ) : null;

  const sidebarContent = (
    <SidebarContent>
      {props.brand ? (
        <>
          <SidebarHeader>
            <BrandSlot>
              <BrandLockup size="small" name={props.brand.name} logo={props.brand.logo} />
            </BrandSlot>
            {headerEnd}
          </SidebarHeader>
          <WorkspaceRow>{workspace}</WorkspaceRow>
        </>
      ) : (
        <SidebarHeader>
          {workspace}
          {headerEnd}
        </SidebarHeader>
      )}

      {!hook.isCompact && (props.search || props.assistant) ? (
        <SearchSlot>
          {props.search ? (
            <SearchButton
              type="button"
              onClick={hook.handleSearchOpen}
              aria-keyshortcuts={props.search.ariaKeyShortcuts}
            >
              <SearchIcon fontSize="small" />
              <SearchLabel>{props.search.label}</SearchLabel>
              {props.search.shortcutHint ? (
                <ShortcutHint aria-hidden="true">{props.search.shortcutHint}</ShortcutHint>
              ) : null}
            </SearchButton>
          ) : null}
          {props.assistant ? (
            <AssistantLink
              href={props.assistant.href}
              onClick={hook.handleAssistantClick}
              aria-current={props.assistant.selected ? 'page' : undefined}
            >
              <AiIcon fontSize="small" color="ai" />
              {props.assistant.label}
            </AssistantLink>
          ) : null}
        </SearchSlot>
      ) : null}

      <Navigation aria-label={hook.navigationLabel} onClick={hook.handleNavigationClick}>
        {props.navigation}
      </Navigation>

      {props.footer || props.account ? (
        <SidebarFooter onClick={hook.handleNavigationClick}>
          {props.footer}
          {props.account ? (
            <AppShellAccountBlock
              account={props.account}
              menuLabel={hook.accountMenuLabel}
              buttonId={hook.accountButtonId}
              menuId={hook.accountMenuId}
              anchor={hook.accountAnchor}
              isOpen={hook.isAccountOpen}
              onOpen={hook.handleAccountOpen}
              onClose={hook.handleAccountClose}
              onItemClick={hook.handleAccountItemClick}
            />
          ) : null}
        </SidebarFooter>
      ) : null}
    </SidebarContent>
  );

  return (
    <Root data-testid="app-shell">
      {hook.isCompact ? (
        <Drawer anchor="left" open={hook.isDrawerOpen} onClose={hook.handleCloseDrawer}>
          <DrawerPanel id={hook.drawerId}>{sidebarContent}</DrawerPanel>
        </Drawer>
      ) : (
        <Sidebar>{sidebarContent}</Sidebar>
      )}

      <Column>
        {hook.isCompact ? (
          <TopBar>
            <IconButton
              tooltip={hook.openNavigationLabel}
              onClick={hook.handleOpenDrawer}
              aria-expanded={hook.isDrawerOpen}
              aria-controls={hook.isDrawerOpen ? hook.drawerId : undefined}
            >
              <MenuIcon />
            </IconButton>
            <Title>{props.title}</Title>
            {props.search ? (
              <IconButton
                tooltip={props.search.label}
                onClick={hook.handleSearchOpen}
                aria-keyshortcuts={props.search.ariaKeyShortcuts}
              >
                <SearchIcon />
              </IconButton>
            ) : null}
            {props.assistant ? (
              <IconButton
                tooltip={props.assistant.label}
                href={props.assistant.href}
                onClick={hook.handleAssistantClick}
                color="ai"
                aria-current={props.assistant.selected ? 'page' : undefined}
              >
                <AiIcon />
              </IconButton>
            ) : null}
            {props.actions ? <Actions>{props.actions}</Actions> : null}
          </TopBar>
        ) : null}

        <Main>{props.children}</Main>
      </Column>
    </Root>
  );
}

export type { Props as AppShellProps };
